import { normalizeDate } from "@features/vessel-call/vessel-call-status/PortServices/flow/utils";
import { endOfDay, startOfDay, subMonths } from "date-fns";
import { type TFunction } from "i18next";
import { z } from "zod";

const LANG_BASE = "vessel-call.port-services";

const shipToShipDocumentFields = {
  sireReportDocuments: "sire-report",
  billOfLadingDocuments: "bill-of-lading",
  cargoManifestDocuments: "cargo-manifest",
  certificateOfOriginDocuments: "certificate-of-origin",
  hoseCertificateDocuments: "hose-certificate",
} as const;

type ShipToShipDocumentField = keyof typeof shipToShipDocumentFields;

type ShipToShipDocument = {
  url: string;
  type?: string;
  certificateType?: string;
  name?: string;
  originalFileName?: string;
  expirationDate?: Date | string | null;
};

type StsData = {
  otherVesselImoNumber?: string;
  otherVesselCallId?: string;
  sireReportIssueDate?: string | Date | null;
  hoseInspectionDate?: string | Date | null;
  sireReportDocuments?: ShipToShipDocument[];
  billOfLadingDocuments?: ShipToShipDocument[];
  cargoManifestDocuments?: ShipToShipDocument[];
  certificateOfOriginDocuments?: ShipToShipDocument[];
  hoseCertificateDocuments?: ShipToShipDocument[];
};

export const validateStsServices = (
  data: StsData,
  ctx: z.RefinementCtx,
  t: TFunction,
  currentVesselImoNumber?: string,
) => {
  const normalizedOtherVesselImoNumber = data.otherVesselImoNumber?.trim();
  const normalizedCurrentVesselImoNumber = currentVesselImoNumber?.trim();

  const requireArrayField = (field: ShipToShipDocumentField) => {
    if ((data[field]?.length ?? 0) > 0) {
      return;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("form-validation.required", {
        label: t(`${LANG_BASE}.fields.${shipToShipDocumentFields[field]}`),
      }),
      path: [field],
    });
  };

  const validateRequiredDate = (
    rawDate: string | Date | null | undefined,
    fieldName: "sireReportIssueDate" | "hoseInspectionDate",
    labelKey: string,
    maxAgeMonths?: number,
  ) => {
    if (!rawDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.required", {
          label: t(`${LANG_BASE}.fields.${labelKey}`),
        }),
        path: [fieldName],
      });
      return;
    }

    const normalized = normalizeDate(rawDate);
    const now = new Date();
    const latestAllowedDate = endOfDay(now);
    const earliestAllowedDate = maxAgeMonths ? startOfDay(subMonths(now, maxAgeMonths)) : null;

    if (!normalized) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.required", {
          label: t(`${LANG_BASE}.fields.${labelKey}`),
        }),
        path: [fieldName],
      });
      return;
    }

    if (normalized > latestAllowedDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.must-not-be-in-future", {
          label: t(`${LANG_BASE}.fields.${labelKey}`),
        }),
        path: [fieldName],
      });
      return;
    }

    if (earliestAllowedDate && normalized < earliestAllowedDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t(`${LANG_BASE}.fields.sire-report-issue-date-window`),
        path: [fieldName],
      });
    }
  };

  if (!normalizedOtherVesselImoNumber) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("form-validation.required", {
        label: t(`${LANG_BASE}.fields.other-vessel-imo-number`),
      }),
      path: ["otherVesselImoNumber"],
    });
  } else if (!/^\d+$/.test(normalizedOtherVesselImoNumber)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("form-validation.invalid-number", {
        label: t(`${LANG_BASE}.fields.other-vessel-imo-number`),
      }),
      path: ["otherVesselImoNumber"],
    });
  } else if (
    normalizedCurrentVesselImoNumber &&
    normalizedOtherVesselImoNumber === normalizedCurrentVesselImoNumber
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("form-validation.cannot-be-same-imo"),
      path: ["otherVesselImoNumber"],
    });
  }

  (Object.keys(shipToShipDocumentFields) as ShipToShipDocumentField[]).forEach(requireArrayField);

  validateRequiredDate(
    data.sireReportIssueDate,
    "sireReportIssueDate",
    "sire-report-issue-date",
    6,
  );
  validateRequiredDate(data.hoseInspectionDate, "hoseInspectionDate", "hose-inspection-date");
};
